/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM 
import { Injectable } from '@angular/core';
import { FilterPersistenceMode, FilterPersistenceOptions, PersistedFilterState } from './filter-framework.models';

@Injectable({ providedIn: 'root' })
export class FilterPersistenceService {
	read(config: FilterPersistenceOptions, recognisedControlNames: readonly string[] = []): PersistedFilterState | null {
		if (!config.enabled || config.mode === FilterPersistenceMode.None || !config.storageKey) return null;
		try {
			const raw = this.storage(config.mode)?.getItem(config.storageKey);
			if (!raw) return null;
			const state = JSON.parse(raw) as PersistedFilterState;
			if (!state || state.schemaVersion !== config.schemaVersion || !state.values || Array.isArray(state.values)) return null;
			const recognised = new Set(recognisedControlNames);
			const values = recognised.size ? Object.fromEntries(Object.entries(state.values).filter(([key]) => recognised.has(key))) : state.values;
			return { ...state, values };
		} catch (error) {
			this.logFailure('read', error);
			return null;
		}
	}

	write(config: FilterPersistenceOptions, values: Record<string, unknown>, expanded?: boolean): void {
		if (!config.enabled || config.mode === FilterPersistenceMode.None || !config.storageKey) return;
		try {
			this.storage(config.mode)?.setItem(config.storageKey, JSON.stringify({
				schemaVersion: config.schemaVersion, values,
				expanded: config.persistExpandedState ? expanded : undefined,
			}));
		} catch (error) { this.logFailure('write', error); }
	}

	clear(config: FilterPersistenceOptions): void {
		if (!config.storageKey || config.mode === FilterPersistenceMode.None) return;
		try { this.storage(config.mode)?.removeItem(config.storageKey); } catch (error) { this.logFailure('clear', error); }
	}

	private storage(mode: FilterPersistenceMode): Storage | null {
		if (typeof window === 'undefined') return null;
		return mode === FilterPersistenceMode.Local ? window.localStorage : window.sessionStorage;
	}

	private logFailure(operation: string, error: unknown): void {
		const detail = error instanceof Error ? error.message : 'Storage is unavailable';
		console.warn(`FilterPersistenceService: unable to ${operation} filter state: ${detail}`);
	}
}
